'use client'

import { useState } from 'react'
import Link from 'next/link'

/**
 * Forgot-password page.
 *
 * The user types an email address; we POST to /api/auth/forgot which
 * silently no-ops for non-customers and triggers a Supabase reset email
 * for known users. The response is identical either way (generic
 * success), so this page also displays the same generic message
 * regardless. No enumeration leak.
 *
 * Once the user clicks the button in the resulting email, they land on
 * /auth/reset (token_hash), which verifies on submit and sets the new
 * password.
 *
 * Design note: audience skews elderly — large high-contrast text, big input,
 * and a clear oversized CTA, consistent with /auth/reset and the landing page.
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [sending, setSending] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return
    setSending(true)
    try {
      await fetch('/api/auth/forgot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      })
    } catch {
      /* same generic response either way — swallow network errors so
         we don't leak whether the request reached the server */
    } finally {
      setSubmitted(true)
      setSending(false)
    }
  }

  const wrap: React.CSSProperties = {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '2rem 1rem', background: 'var(--color-bg)',
  }
  const card: React.CSSProperties = {
    width: '100%', maxWidth: 480, padding: '2.5rem',
    background: 'var(--color-bg-card)', border: '1px solid var(--color-border)',
    borderRadius: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
  }
  const input: React.CSSProperties = {
    width: '100%', padding: '1.15rem 1.15rem', borderRadius: 12, minHeight: 60,
    border: '2px solid var(--color-border)', background: 'var(--color-bg-secondary)',
    color: 'var(--color-text-primary)', fontSize: '1.25rem', lineHeight: 1.4,
  }
  const buttonStyle: React.CSSProperties = {
    padding: '1.2rem 1.25rem', fontSize: '1.375rem', fontWeight: 700,
    borderRadius: 12, minHeight: 62, width: '100%', letterSpacing: '0.01em',
  }
  const backLink: React.CSSProperties = {
    display: 'inline-block', fontSize: '1.0625rem', fontWeight: 600,
    color: 'var(--color-accent)', textDecoration: 'underline', textUnderlineOffset: 3,
  }

  return (
    <div style={wrap}>
      <div style={card}>
        <h1 style={{ margin: '0 0 0.75rem', fontSize: '1.75rem', fontWeight: 800, lineHeight: 1.2 }}>🔒 Passwort zurücksetzen</h1>

        {submitted ? (
          <>
            <p style={{ fontSize: '1.1875rem', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
              Wenn die Adresse <strong>{email}</strong> bei uns registriert ist, haben wir gerade einen Reset-Link an dich geschickt. Schau in dein Postfach (auch Spam) und klicke auf den Button in der E-Mail — der Link ist eine Stunde gültig.
            </p>
            <p style={{ fontSize: '1rem', color: 'var(--color-text-muted)', marginTop: '1.25rem', lineHeight: 1.6 }}>
              Du bekommst keine Mail, wenn die Adresse nicht in unserem System hinterlegt ist. Das ist Absicht — wir verraten nicht, wer ein Konto hat. Wenn das passiert: <a href="mailto:support@fuchs.finance" style={{ color: 'var(--color-accent)', fontWeight: 600 }}>support@fuchs.finance</a>.
            </p>
            <Link href="/" style={{ ...backLink, marginTop: '1.5rem' }}>
              ← Zur Anmeldung
            </Link>
          </>
        ) : (
          <>
            <p style={{ fontSize: '1.1875rem', color: 'var(--color-text-secondary)', lineHeight: 1.6, marginTop: 0 }}>
              Tippe deine E-Mail-Adresse ein. Wenn du bei uns registriert bist, schicken wir dir einen Link zum Zurücksetzen.
            </p>
            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: '1.125rem', marginTop: '1.5rem' }}>
              <div>
                <label htmlFor="forgot-email" style={{ display: 'block', fontSize: '1.125rem', fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: '0.5rem' }}>E-Mail-Adresse</label>
                <input id="forgot-email" type="email" value={email} onChange={e => setEmail(e.target.value)} autoFocus required autoComplete="email" placeholder="name@example.com" style={input} />
              </div>
              <button type="submit" disabled={sending || !email.trim()} className="btn-primary" style={buttonStyle}>
                {sending ? '⏳ Wird gesendet…' : 'Reset-Link senden'}
              </button>
              <Link href="/" style={{ ...backLink, alignSelf: 'flex-start' }}>
                ← Zurück zur Anmeldung
              </Link>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
